import React, { useState, useMemo, useCallback } from 'react';
import { doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { getMinimumPrice, getTestSongMinimumPrice, formatMinimumPrice } from '@/lib/currencyConfig';
import { Coins } from 'lucide-react';

function songFlags(song) {
  const t = (song.title || '').trim().toLowerCase();
  const a = (song.original_artist || song.artist || '').trim().toLowerCase();
  return {
    isFree: t === 'free' && a === 'free',
    isTest: t === 'test' && a === 'test',
  };
}

/**
 * Compact control to set one price for catalogue songs (internal rules for special rows unchanged).
 */
export default function BatchSongPricesButton({
  songs,
  currency,
  format,
  onApplied,
  className = '',
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const minimumPrice = getMinimumPrice(currency);
  const testSongMinimum = getTestSongMinimumPrice(currency);
  const formattedMinimum = formatMinimumPrice(currency);

  const activeSongCount = useMemo(
    () => (songs || []).filter((s) => !s.archived).length,
    [songs]
  );

  const apply = useCallback(async () => {
    const priceNum = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      toast({ title: 'Check the amount', description: 'Enter a valid price.', variant: 'destructive' });
      return;
    }

    const targets = (songs || []).filter((s) => !s.archived);
    if (targets.length === 0) {
      toast({ title: 'Nothing to update', description: 'Add songs to your catalogue first.', variant: 'destructive' });
      return;
    }

    const wouldUpdate = [];
    const skippedInternal = [];
    for (const song of targets) {
      const { isFree, isTest } = songFlags(song);
      if (isFree) {
        skippedInternal.push(song.id);
        continue;
      }
      if (isTest && priceNum < testSongMinimum) {
        toast({
          title: 'Try a slightly higher amount',
          description: 'That price is too low for one or more songs in your list — bump it up a touch and try again.',
          variant: 'destructive',
        });
        return;
      }
      if (!isTest && priceNum < minimumPrice) {
        toast({
          title: 'Bump it up a little',
          description: `Song requests on Your Song Request start from ${formattedMinimum}. Enter at least that to update your catalogue.`,
          variant: 'destructive',
        });
        return;
      }
      wouldUpdate.push(song);
    }

    if (wouldUpdate.length === 0) {
      toast({
        title: 'Nothing to update yet',
        description:
          skippedInternal.length > 0
            ? 'Add more songs to your catalogue to use this shortcut.'
            : 'Nothing matched.',
        variant: 'destructive',
      });
      return;
    }

    setBusy(true);
    try {
      const batch = writeBatch(db);
      const ts = serverTimestamp();
      for (const song of wouldUpdate) {
        batch.update(doc(db, 'songs', song.id), { price: priceNum, updated_at: ts });
      }
      await batch.commit();
      const n = wouldUpdate.length;
      const desc =
        skippedInternal.length > 0
          ? `${n} song${n === 1 ? '' : 's'} are now ${format(priceNum)} per request. A few listings weren’t changed.`
          : `${n} song${n === 1 ? '' : 's'} are now ${format(priceNum)} per request.`;
      toast({ title: 'You’re all set', description: desc });
      setOpen(false);
      setValue('');
      await onApplied?.();
    } catch (e) {
      console.error(e);
      toast({
        title: 'Could not update',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  }, [
    value,
    songs,
    minimumPrice,
    testSongMinimum,
    formattedMinimum,
    currency,
    format,
    toast,
    onApplied,
  ]);

  if (!songs?.length) return null;

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={`h-8 shrink-0 px-2 text-xs text-gray-500 hover:text-gray-200 ${className}`}
        onClick={() => setOpen(true)}
        title="Set the same request price on every song in one go"
      >
        <Coins className="w-3.5 h-3.5 mr-1 opacity-80" />
        <span className="hidden sm:inline">All prices</span>
        <span className="sm:hidden">Prices</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Same price for your whole catalogue</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm leading-relaxed">
              Set what you want fans to pay per request — we’ll roll it out across your active song list in one tap so
              you’re not editing tracks one by one. If an amount isn’t allowed, we’ll tell you before anything changes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="batch-price" className="text-gray-300 text-sm">
              Price per request ({currency})
            </Label>
            <Input
              id="batch-price"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="bg-gray-800 border-gray-600 text-white"
              placeholder={format(minimumPrice)}
            />
            {activeSongCount > 0 && (
              <p className="text-xs text-gray-500">{activeSongCount} songs in your active catalogue.</p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" className="bg-blue-600 hover:bg-blue-700" onClick={apply} disabled={busy}>
              {busy ? 'Saving…' : 'Update catalogue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
